/** Lightweight particle system: floating text, sparkles, pop rings, crow rings. Visual only. */

export type ParticleKind = 'text' | 'sparkle' | 'ring' | 'crow';

interface Particle {
  kind: ParticleKind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  born: number;
  life: number;
  text: string;
  color: string;
  size: number;
}

const MAX = 120;
const particles: Particle[] = [];

export function emitParticle(p: Partial<Particle> & { kind: ParticleKind; x: number; y: number }, now: number): void {
  if (particles.length >= MAX) particles.shift();
  particles.push({ vx: 0, vy: -30, life: 1200, text: '', color: '#ffd54a', size: 16, ...p, born: now });
}

export function floatText(x: number, y: number, text: string, color: string, now: number, size = 16): void {
  emitParticle({ kind: 'text', x, y, text, color, size, vy: -38, life: 1300 }, now);
}

export function sparkles(x: number, y: number, color: string, now: number, n = 8): void {
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    emitParticle({ kind: 'sparkle', x, y, vx: Math.cos(a) * 70, vy: Math.sin(a) * 70, color, life: 600, size: 3 }, now);
  }
}

export function popRing(x: number, y: number, color: string, now: number): void {
  emitParticle({ kind: 'ring', x, y, vx: 0, vy: 0, color, life: 450, size: 26 }, now);
}

export function crowRing(x: number, y: number, now: number): void {
  emitParticle({ kind: 'crow', x, y, vx: 0, vy: 0, color: '#ffcf40', life: 1100, size: 180 }, now);
}

export function drawParticles(ctx: CanvasRenderingContext2D, now: number): void {
  for (let i = particles.length - 1; i >= 0; i--) {
    const p = particles[i]!;
    const age = now - p.born;
    if (age >= p.life) {
      particles.splice(i, 1);
      continue;
    }
    const t = age / p.life;
    const dt = age / 1000;
    ctx.save();
    ctx.globalAlpha = 1 - t;
    if (p.kind === 'text') {
      ctx.font = `bold ${p.size}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(0,0,0,0.6)';
      const y = p.y + p.vy * dt;
      ctx.strokeText(p.text, p.x, y);
      ctx.fillStyle = p.color;
      ctx.fillText(p.text, p.x, y);
    } else if (p.kind === 'sparkle') {
      ctx.fillStyle = p.color;
      ctx.fillRect(p.x + p.vx * dt - p.size / 2, p.y + p.vy * dt - p.size / 2, p.size, p.size);
    } else {
      ctx.strokeStyle = p.color;
      ctx.lineWidth = p.kind === 'crow' ? 4 * (1 - t) + 1 : 3;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 6 + p.size * t, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  }
}

export function clearParticles(): void {
  particles.length = 0;
}
